import type { PosSaleKind, PosSaleStatus } from "@station/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";

/**
 * Yerel kasa (POS) yönetim ekranlarının sorguları (Prompt 15 — web).
 *
 * Okuma `pos.read`; kampanya / hızlı tuş / kasa ayarı `pos.manage`; personel PIN'i `pos.staff.pin.manage`
 * (ayrıca **step-up** ister). Modül `LOCAL_POS` bayrağına bağlıdır; kapalıysa API `403 FEATURE_DISABLED`
 * döner ve ekranlar hiç istek atmadan boş durum çizer.
 *
 * **Tazeleme:** kasa fişi ajan senkronuyla düşer ve HER satış bir olay üretir (`pos.sale.recorded`) →
 * gerçek zamanlı geçersiz kılma yeterlidir (`lib/realtime.ts > INVALIDATIONS`). Yine de ajan çevrimdışıyken
 * biriken partiler toplu düştüğü için liste ve rapor pencere odağında yeniden çekilir.
 */

export function usePosPermissions(): {
  enabled: boolean;
  canRead: boolean;
  canManage: boolean;
  canManagePin: boolean;
} {
  const permissions = usePermissions();
  return {
    enabled: useFeatures().has("LOCAL_POS"),
    canRead: permissions.has("pos.read"),
    canManage: permissions.has("pos.manage"),
    canManagePin: permissions.has("pos.staff.pin.manage"),
  };
}

// ── Kasa satışları ───────────────────────────────────────────────────────────
export interface PosSaleFilters {
  stationId?: string | undefined;
  staffId?: string | undefined;
  shiftId?: string | undefined;
  status?: PosSaleStatus | undefined;
  kind?: PosSaleKind | undefined;
  businessDateFrom?: string | undefined;
  businessDateTo?: string | undefined;
  flaggedOnly?: boolean | undefined;
}

export function posSalesQuery(
  companyId: string,
  filters: PosSaleFilters,
  cursor: string | undefined,
  limit = 25,
): Record<string, string | number> {
  return {
    companyId,
    limit,
    ...(filters.stationId ? { stationId: filters.stationId } : {}),
    ...(filters.staffId ? { staffId: filters.staffId } : {}),
    ...(filters.shiftId ? { shiftId: filters.shiftId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.businessDateFrom ? { businessDateFrom: filters.businessDateFrom } : {}),
    ...(filters.businessDateTo ? { businessDateTo: filters.businessDateTo } : {}),
    ...(filters.flaggedOnly ? { flaggedOnly: "true" } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

export function usePosSales(companyId: string, filters: PosSaleFilters, cursor?: string, enabled = true) {
  const query = posSalesQuery(companyId, filters, cursor);
  return useQuery({
    queryKey: queryKeys.pos(companyId, "sales", query),
    enabled,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/pos/sales", { params: { query: query as never } })).data!,
  });
}

export function usePosSale(id: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.pos(id ?? "none", "sale"),
    enabled: enabled && id !== null,
    queryFn: async () => (await api.GET("/pos/sales/{id}", { params: { path: { id: id! } } })).data!,
  });
}

/**
 * Satış listesinin KASİYER süzgeci için personel seçenekleri. Personel kartları VARDİYA modülünündür
 * (`shifts.read`); yalnızca `pos.read` yetkisi olan kullanıcıda uç 403 döndüreceği için sorgu hiç
 * açılmaz ve süzgeç çizilmez (ekran bu yüzden bozulmaz).
 */
export function usePosStaffOptions(companyId: string, stationId: string | null) {
  const canReadStaff = usePermissions().has("shifts.read");
  const query = {
    companyId,
    limit: 200,
    isActive: "true" as const,
    ...(stationId ? { stationId } : {}),
  };
  return useQuery({
    queryKey: queryKeys.pos(companyId, "sales", { staffOptions: query }),
    enabled: canReadStaff,
    staleTime: 300_000,
    queryFn: async () => (await api.GET("/shifts/staff", { params: { query: query as never } })).data!.items,
  });
}

// ── Gün × istasyon × kasiyer raporu ──────────────────────────────────────────
export function usePosReport(
  companyId: string,
  range: { from: string; to: string; stationId?: string | undefined },
  enabled = true,
) {
  const query = {
    companyId,
    from: range.from,
    to: range.to,
    ...(range.stationId ? { stationId: range.stationId } : {}),
  };
  return useQuery({
    queryKey: queryKeys.pos(companyId, "report", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/pos/reports/summary", { params: { query } })).data!,
  });
}

// ── Kasa ayarları ────────────────────────────────────────────────────────────
export function usePosSettings(companyId: string, stationId: string | null, enabled = true) {
  const query = { companyId, ...(stationId ? { stationId } : {}) };
  return useQuery({
    queryKey: queryKeys.pos(companyId, "settings", query),
    enabled,
    queryFn: async () => (await api.GET("/pos/settings", { params: { query } })).data!,
  });
}

// ── Kampanyalar ──────────────────────────────────────────────────────────────
export interface CampaignFilters {
  stationId?: string | undefined;
  isActive?: boolean | undefined;
  currentOnly?: boolean | undefined;
}

export function useCampaigns(companyId: string, filters: CampaignFilters, cursor?: string, enabled = true) {
  const query: Record<string, string | number> = {
    companyId,
    limit: 25,
    ...(filters.stationId ? { stationId: filters.stationId } : {}),
    ...(filters.isActive === undefined ? {} : { isActive: filters.isActive ? "true" : "false" }),
    ...(filters.currentOnly ? { currentOnly: "true" } : {}),
    ...(cursor ? { cursor } : {}),
  };
  return useQuery({
    queryKey: queryKeys.pos(companyId, "campaigns", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/campaigns", { params: { query: query as never } })).data!,
  });
}

// ── Personel PIN'i ───────────────────────────────────────────────────────────
/**
 * PIN'in KENDİSİ hiçbir yanıtta dönmez: yalnızca "var mı", "ne zaman atandı" ve "kaç yanlış deneme".
 * Pencere kapalıyken sorgulanmaz ve önbellekte bekletilmez (yanlış deneme sayacı canlı okunmalı).
 */
export function useStaffPin(staffId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.pos(staffId ?? "none", "staff-pin"),
    enabled: enabled && staffId !== null,
    staleTime: 0,
    queryFn: async () => (await api.GET("/pos/staff/{id}/pin", { params: { path: { id: staffId! } } })).data!,
  });
}

/**
 * Bir kasa işlemi birden çok ekranı birden değiştirir (satış listesi, özet raporu, kampanya listesi,
 * kasa ayarı) ve PIN / kampanya değişikliği VARDİYA ekranlarını da etkiler (personel penceresi, kasiyer
 * hesabının beklenen tutarı): modülün tüm önbelleği ve vardiya önbelleği birlikte tazelenir.
 */
export function useRefreshPos(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.pos() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.shifts() }),
    ]);
  };
}
